import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { ArrowDownCircle, ArrowUpCircle, Search, Wallet } from "lucide-react";

export default function Cash() {
    const [accounts, setAccounts] = useState(null);
    const [txns, setTxns] = useState(null);
    const [selectedAcc, setSelectedAcc] = useState("");
    const [type, setType] = useState("");
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/cash/accounts").then((r) => setAccounts(r.data));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();
        if (selectedAcc) params.set("account_id", selectedAcc);
        if (type) params.set("type", type);
        const qs = params.toString();
        api.get(`/cash/transactions${qs ? "?" + qs : ""}`).then((r) => setTxns(r.data));
    }, [selectedAcc, type]);

    if (!accounts) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING CASH BOOK...</div>;

    const filteredTxns = (txns?.transactions || []).filter((t) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return t.party.toLowerCase().includes(s) || t.narration.toLowerCase().includes(s) || (t.voucher || "").toLowerCase().includes(s);
    });

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// CASH BOOK</div>
                <h1 className="font-display font-black text-3xl">Cash-in-Hand & Site Imprest</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    HO safe + 4 site imprest accounts · <Link to="/banking" className="text-[var(--brand-amber)] hover:underline">Banking →</Link>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="cash-balance" label="Total Cash-in-Hand" value={accounts.summary.total_balance} sub={`${accounts.summary.count} locations`} accent />
                <KpiCard testId="cash-limit" label="Total Imprest Limit" value={accounts.summary.total_limit} sub="Approved ceiling" />
                <KpiCard testId="cash-in" label="Receipts (Period)" value={txns?.summary.total_in || 0} sub="Cash In" />
                <KpiCard testId="cash-out" label="Payments (Period)" value={txns?.summary.total_out || 0} sub="Cash Out" />
            </div>

            <Panel testId="cash-accounts" title="Cash Accounts" subtitle="HO + Site imprest wallets">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {accounts.accounts.map((a) => {
                        const util = a.limit ? (a.balance / a.limit) * 100 : 0;
                        return (
                            <button
                                key={a.id}
                                data-testid={`cash-card-${a.id}`}
                                onClick={() => setSelectedAcc(selectedAcc === a.id ? "" : a.id)}
                                className={`text-left border p-3 hover:border-[var(--brand-amber)] transition-colors ${selectedAcc === a.id ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel"}`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <Wallet size={14} className="text-[var(--brand-amber)]" />
                                    <span className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">{a.id}</span>
                                </div>
                                <div className="font-display font-bold text-[var(--text-primary)] text-sm leading-tight">{a.name}</div>
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-1">
                                    {a.location} · {a.custodian}
                                </div>
                                <div className="mt-3 flex items-baseline justify-between">
                                    <span className="font-mono-fin text-xl text-[var(--text-primary)]">{fmtINR(a.balance)}</span>
                                    <span className="text-[10px] font-mono-fin text-[var(--text-muted)]">/ {fmtINR(a.limit)}</span>
                                </div>
                                <div className="mt-2 h-1 bg-[var(--bg-base)] border border-panel">
                                    <div className="h-full" style={{ width: `${Math.min(100, util)}%`, background: util > 80 ? "#ff433d" : util > 60 ? "#fb8b1e" : "#4af6c3" }} />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </Panel>

            <Panel testId="cash-txns" title="Cash Transactions" subtitle={selectedAcc ? `Filtered · ${selectedAcc}` : "All locations"} right={
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                        <input
                            data-testid="cash-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search party / voucher"
                            className="bg-[var(--bg-base)] border border-panel pl-7 pr-2 py-1 text-xs font-mono-fin w-44"
                        />
                    </div>
                    <select
                        data-testid="cash-account-filter"
                        value={selectedAcc}
                        onChange={(e) => setSelectedAcc(e.target.value)}
                        className="bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs font-mono-fin uppercase tracking-wider"
                    >
                        <option value="">All Locations</option>
                        {accounts.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <select
                        data-testid="cash-type-filter"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs font-mono-fin uppercase tracking-wider"
                    >
                        <option value="">All</option>
                        <option value="In">Receipt</option>
                        <option value="Out">Payment</option>
                    </select>
                </div>
            }>
                <div className="overflow-x-auto">
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel">
                            <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-2">Date</th>
                                <th className="text-left">Voucher</th>
                                <th className="text-left">Location</th>
                                <th className="text-left">Party / Narration</th>
                                <th className="text-left">Ref</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTxns.map((t) => (
                                <tr key={t.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                    <td className="py-2 text-[var(--text-secondary)]">{fmtDate(t.date)}</td>
                                    <td>
                                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${t.type === "In" ? "border-pos text-pos" : "border-neg text-neg"}`} style={{ borderColor: t.type === "In" ? "#4af6c3" : "#ff433d", color: t.type === "In" ? "#4af6c3" : "#ff433d" }}>{t.voucher}</span>
                                    </td>
                                    <td className="text-[var(--text-primary)] text-[11px]">{t.location}</td>
                                    <td className="text-[var(--text-primary)]">
                                        <div className="flex items-center gap-2">
                                            {t.type === "In" ? <ArrowDownCircle size={12} className="text-pos" /> : <ArrowUpCircle size={12} className="text-neg" />}
                                            <div>
                                                <div>{t.party}</div>
                                                <div className="text-[10px] text-[var(--text-muted)]">{t.narration}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-[var(--text-secondary)] text-[10px]">{t.ref}</td>
                                    <td className={`text-right ${t.type === "In" ? "text-pos" : "text-neg"}`}>
                                        {t.type === "In" ? "+" : "−"}{fmtINR(t.amount)}
                                    </td>
                                </tr>
                            ))}
                            {filteredTxns.length === 0 && (
                                <tr><td colSpan={6} className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs">No cash transactions match filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Panel>
        </div>
    );
}
